import _ from 'lodash';

import { date as commonDate } from '@finley-technologies/common';
import { recurringDate } from '@finley-technologies/finley-date';
import { Date as dateType, Util } from '@finley-technologies/finley-types';

import {
  DayOfTheWeek,
  dayOfTheWeekToNumberMap,
  ConfigFormState,
} from './types';

export function ordinalSuffix(num: number) {
  const tenth = num % 10;
  const hundredth = num % 100;
  if (tenth === 1 && hundredth !== 11) {
    return `${num}st`;
  }
  if (tenth === 2 && hundredth !== 12) {
    return `${num}nd`;
  }
  if (tenth === 3 && hundredth !== 13) {
    return `${num}rd`;
  }
  return `${num}th`;
}

export function makeAdverb(word: string) {
  return `${word[0].toUpperCase() + word.slice(1).toLowerCase()}ly`;
}

export const generateHumanReadableString = ({
  cadence,
  offsetDayUnit,
  offsetDays,
  period,
  startDate,
  startDateNumber,
  endDate,
  frequency,
  selectedDays,
  occurrence,
}: {
  cadence: number;
  offsetDayUnit: dateType.DayUnit;
  offsetDays: number;
  period: dateType.RecurringDate.BaseDateConfigType;
  startDate?: string;
  startDateNumber?: number;
  endDate?: string;
  frequency?: dateType.RecurringDate.FrequencyPeriod;
  selectedDays?: DayOfTheWeek[];
  occurrence?: number;
}): string => {
  if (!frequency || !startDate || !endDate) {
    return 'Set a recurring date.';
  }
  const offsetString =
    Number(offsetDays) !== 0
      ? `${Math.abs(offsetDays)} ${offsetDayUnit.slice(0, -3)} day${
          offsetDays > 1 ? 's' : ''
        } ${offsetDays < 0 ? 'before' : 'after'}`
      : '';
  const variableFrequencyString = `${frequency}, every ${cadence} ${frequency}${
    cadence > 1 ? 's' : ''
  } from ${startDate} to ${endDate}.`;

  const selectedDaysString = selectedDays
    ? selectedDays
        .sort((a, b) => dayOfTheWeekToNumberMap[a] - dayOfTheWeekToNumberMap[b])
        .join(', ')
    : null;
  if (period === 'weekday' && selectedDaysString) {
    return `${offsetString}${
      offsetString === '' ? 'Every' : ' every'
    } ${selectedDaysString}, every ${cadence} ${frequency}${
      cadence > 1 ? 's' : ''
    } from ${startDate} to ${endDate}.`;
  }
  if (period === 'weekdayByOccurrence' && occurrence && selectedDaysString) {
    return `${offsetString}${
      offsetString === '' ? 'The' : ' the'
    } ${ordinalSuffix(
      occurrence,
    )} ${selectedDaysString} of each ${variableFrequencyString}`;
  }
  if (period === 'calendarDate') {
    return `${offsetString}${offsetString === '' ? 'The' : ' the'} ${
      startDateNumber ? ordinalSuffix(startDateNumber) : startDate
    } of each ${variableFrequencyString}`;
  }
  if (period === 'startOf' || period === 'endOf') {
    return `${offsetString}${
      offsetString === '' ? 'The' : ' the'
    } ${period.toLowerCase().slice(0, -2)} of each ${variableFrequencyString}`;
  }
  return 'Set a recurring date.';
};

export function downloadCSVWithoutHeader(
  filename: string,
  data: Util.FullDateStr[],
) {
  const todayFullDateStr = commonDate.currentDateStr();

  const dataWithDoubleQuotes = data.map((d) => JSON.stringify(d));
  const blob = new Blob([dataWithDoubleQuotes.join('\n')], {
    type: 'text/csv',
  });
  const url = window.URL.createObjectURL(blob);

  const tag = document.createElement('a');
  tag.setAttribute('href', url);
  tag.setAttribute('download', `${filename}-${todayFullDateStr}.csv`);
  tag.click();
}

export function downloadRelativeConfig(
  relativeConfig: dateType.RecurringDate.RelativeConfig,
) {
  const todayFullDateStr = commonDate.currentDateStr();

  const blob = new Blob([JSON.stringify(relativeConfig)], {
    type: 'application/json',
  });
  const url = window.URL.createObjectURL(blob);

  const tag = document.createElement('a');
  tag.setAttribute('href', url);
  tag.setAttribute(
    'download',
    `relative-recurring-date-config-${todayFullDateStr}.json`,
  );
  tag.click();
}

export function configIsNotNull(
  value: dateType.RecurringDate.BaseDateConfig | null,
): value is dateType.RecurringDate.BaseDateConfig {
  return value !== null;
}

export function dayOfTheWeekNotUndefined(
  day: DayOfTheWeek | undefined,
): day is DayOfTheWeek {
  return day !== undefined;
}

export function isBaseRecurringDateConfig(
  config: dateType.RecurringDate.BaseRecurringDateConfig | Util.FullDateStr[],
): config is dateType.RecurringDate.BaseRecurringDateConfig {
  return !(config instanceof Array);
}

export function isFullDateStrArray(
  config: dateType.RecurringDate.BaseRecurringDateConfig | Util.FullDateStr[],
): config is Util.FullDateStr[] {
  return config instanceof Array;
}

export function getDayOfTheWeekFromWeekday(weekday: dateType.Weekday) {
  return (Object.keys(dayOfTheWeekToNumberMap) as Array<DayOfTheWeek>).find(
    (key) => dayOfTheWeekToNumberMap[key] === weekday,
  );
}

export function convertConfigFormStatesToRecurringDateConfigs(
  configs: ConfigFormState[],
): dateType.RecurringDate.BaseDateConfig[] {
  const recurringDateConfigs: dateType.RecurringDate.BaseDateConfig[] = [];
  for (const config of configs) {
    const result = recurringDate.validateAndCreateConfig(
      config.cadence || 1,
      config.offsetDays || 0,
      config.offsetDayUnit || 'calendarDay',
      config.period,
      config.selectedDays
        ? config.selectedDays.map((day) => dayOfTheWeekToNumberMap[day])
        : [],
      config.occurrence,
      config.startDate
        ? commonDate.dateToFullDateString(config.startDate)
        : undefined,
      config.endDate
        ? commonDate.dateToFullDateString(config.endDate)
        : undefined,
      config.frequency,
    );
    if (result.config) {
      recurringDateConfigs.push(result.config);
    }
  }
  return recurringDateConfigs;
}

export function convertConfigFormStatesToRelativeRecurringDateConfigs(
  configs: ConfigFormState[],
): dateType.RecurringDate.RelativeDateConfig[] {
  const relativeDateConfigs: dateType.RecurringDate.RelativeDateConfig[] = [];
  for (const config of configs) {
    const result = recurringDate.validateAndCreateRelativeConfig(
      config.cadence || 1,
      config.offsetDays || 0,
      config.offsetDayUnit || 'calendarDay',
      config.period,
      config.selectedDays
        ? config.selectedDays.map((day) => dayOfTheWeekToNumberMap[day])
        : [],
      config.occurrence,
      config.startDateFormula,
      config.endDateFormula,
      config.frequency,
    );
    if (result.config) {
      relativeDateConfigs.push(result.config);
    }
  }
  return relativeDateConfigs;
}

export function convertRecurringDateConfigsToConfigFormStates(
  configs: dateType.RecurringDate.BaseRecurringDateConfig[],
): ConfigFormState[] {
  return configs.map((c) => ({
    isValid: true,
    isRelative: false,
    startDate: commonDate.fullDateStrToDate(c.startDate),
    endDate: commonDate.fullDateStrToDate(c.endDate),
    frequency: c.period,
    cadence: c.cadence,
    occurrence: c.type === 'weekdayByOccurrence' ? c.occurrence : undefined,
    period: c.type,
    offsetDays: c.offset?.days,
    offsetDayUnit: c.offset?.dayUnit,
    selectedDays:
      c.type === 'weekday' || c.type === 'weekdayByOccurrence'
        ? _.map(c.weekdays, (weekday) =>
            getDayOfTheWeekFromWeekday(weekday),
          ).filter(dayOfTheWeekNotUndefined)
        : undefined,
  }));
}

export function convertRelativeRecurringDateConfigsToConfigFormStates(
  configs: dateType.RecurringDate.RelativeDateConfig[],
): ConfigFormState[] {
  return configs.map((c) => ({
    isValid: true,
    isRelative: true,
    startDateFormula: c.startDateFormula,
    endDateFormula: c.endDateFormula,
    frequency: c.baseDateConfig.period,
    cadence: c.baseDateConfig.cadence,
    occurrence:
      c.baseDateConfig.type === 'weekdayByOccurrence'
        ? c.baseDateConfig.occurrence
        : undefined,
    period: c.baseDateConfig.type,
    offsetDays: c.baseDateConfig.offset?.days,
    offsetDayUnit: c.baseDateConfig.offset?.dayUnit,
    selectedDays:
      c.baseDateConfig.type === 'weekday' ||
      c.baseDateConfig.type === 'weekdayByOccurrence'
        ? _.map(c.baseDateConfig.weekdays, (weekday) =>
            getDayOfTheWeekFromWeekday(weekday),
          ).filter(dayOfTheWeekNotUndefined)
        : undefined,
  }));
}

export function createNonRelativeConfigFormStatesToTestTriggerDate(
  triggerDate: Util.FullDateStr,
  configs: ConfigFormState[],
): ConfigFormState[] {
  const recurringDateConfigs = convertConfigFormStatesToRelativeRecurringDateConfigs(
    configs.filter((c) => c.isValid && c.isRelative),
  ).map((c) => {
    const result = recurringDate.getStartAndEndDateFromTriggerDate(
      triggerDate,
      c,
    );
    return {
      startDate: result.startDate,
      endDate: result.endDate,
      ...c.baseDateConfig,
    };
  });

  return convertRecurringDateConfigsToConfigFormStates(recurringDateConfigs);
}
